import { tierForLevel, MAX_TIER } from '@ringmaster/shared';

/**
 * LevelDiamond — the lifetime-progression badge.
 *
 * One component, every surface: pinned under a player's medallion, on the END
 * ceremony's bar caps, and blown up on the profile header. It is the ONLY place
 * a level is drawn, for the same reason `RolePortrait` is the only place a role
 * is — two of these would drift apart within a week.
 *
 * Renders NOTHING for a null/absent level. That is the whole "no badge until
 * your first game" rule: `PlayerPublic.level` is absent until a player has
 * finished one, so no caller has to remember the rule (see shared/state.ts).
 *
 * SVG rather than a rotated div: the design is a double stroke (thick outer,
 * hairline inner) and a rotated box would need counter-rotated text, two nested
 * elements and a non-uniform border. Here it is four points and a `<text>`.
 */

export interface LevelTier {
  /** Thick outer stroke — the tier's identity colour. */
  border: string;
  /** Hairline inner stroke, one step in from the outer. */
  inner: string;
  /** The numeral. */
  text: string;
  /** Halo colour when `glow` is set. */
  glow: string;
  /** Human name — used by the profile header and aria labels. */
  label: string;
}

/**
 * One band per 5 levels (`tierForLevel`). Every colour is an EXISTING token ramp
 * — bronze → gold → arcana → verdigris → blood → bone. Shaped like
 * `ROLE_TALLY_ACCENT` in WhisperRoleTally.tsx, which is the sanctioned pattern
 * for a per-variant accent table.
 */
export const LEVEL_TIERS: Record<number, LevelTier> = {
  0: {
    border: 'var(--gold-2, #7a4c1e)',
    inner: 'var(--gold-1, #57351a)',
    text: '#d9a58f',
    glow: 'rgba(122, 76, 30, 0.55)',
    label: 'Bronze',
  },
  1: {
    border: 'var(--gold-5, #e6b866)',
    inner: 'var(--gold-4, #c9923f)',
    text: 'var(--gold-6, #f7e3b0)',
    glow: 'rgba(230, 184, 102, 0.6)',
    label: 'Gold',
  },
  2: {
    border: 'var(--arcana-3, #8b5cf6)',
    inner: 'var(--arcana-2, #5b3fa8)',
    text: 'var(--arcana-4, #c9b6ff)',
    glow: 'rgba(139, 92, 246, 0.6)',
    label: 'Arcana',
  },
  3: {
    border: 'var(--verdigris-3, #3d7a6c)',
    inner: 'var(--verdigris-2, #1e4f47)',
    text: '#7fd4c1',
    glow: 'rgba(61, 122, 108, 0.6)',
    label: 'Verdigris',
  },
  4: {
    border: 'var(--blood-5, #c22f3e)',
    inner: 'var(--blood-3, #802636)',
    text: 'var(--blood-6, #ff5a4f)',
    glow: 'rgba(194, 47, 62, 0.6)',
    label: 'Blood',
  },
  5: {
    border: '#cfc4bb',
    inner: 'var(--state-dead, #9c8f86)',
    text: '#f4efe9',
    glow: 'rgba(207, 196, 187, 0.55)',
    label: 'Bone',
  },
};

/**
 * Where the numeral's baseline anchor goes — NOT the diamond's centre (50).
 *
 * `dominantBaseline="central"` centres the EM BOX, not the ink, and Grenze ships
 * old-style figures: 3, 4, 7 and 9 hang below the baseline while 6 and 8 climb.
 * Measured in the browser at the sizes this ships at, the ink centre of a digit
 * anchored at y=50 lands at 54–58 — visibly low inside a shape whose widest
 * point is exactly the midline. 44.5 pulls the mean ink centre back onto 50.
 *
 * A fixed viewBox offset, deliberately, not an `em` one: the error is a constant
 * ~5.5 units at every `fontSize` below (they differ by less than half a unit),
 * so one number keeps a 1-digit and a 2-digit badge sitting at the same height.
 * No single offset can centre every digit — with old-style figures "3" and "6"
 * simply have different ink centres — so this centres the SET.
 */
const TEXT_Y = 44.5;

/** The tier a level wears. Exported so callers can preview the NEXT one. */
export function tierOf(level: number): LevelTier {
  return LEVEL_TIERS[tierForLevel(level)] ?? LEVEL_TIERS[MAX_TIER]!;
}

export interface LevelDiamondProps {
  /** Absent/null renders nothing — a player with no finished game has no badge. */
  level?: number | null;
  /** Width AND height in px. Default 26 (the medallion pin size). */
  size?: number;
  /** Outer halo. Use for the local player and the ceremony's current-level cap. */
  glow?: boolean;
  /**
   * Outline-only, muted — the ceremony's "next level" cap. It still wears the
   * tier it WILL be, which is what turns one more level into a visible reward
   * rather than a bigger number.
   */
  dim?: boolean;
  className?: string;
  /** Accessible label. Defaults to "Level N". */
  title?: string;
}

export function LevelDiamond({
  level,
  size = 26,
  glow = false,
  dim = false,
  className,
  title,
}: LevelDiamondProps) {
  if (level == null || !Number.isFinite(level) || level < 1) return null;

  const n = Math.floor(level);
  const tier = tierOf(n);
  const label = String(n);
  // Three digits still has to fit inside a rhombus, which is a lot narrower than
  // its bounding box at the vertical midline. Sized generously because the badge
  // is drawn small (≈26px pinned under a medallion) — a timid numeral is
  // unreadable at the size this thing actually ships at.
  const fontSize = label.length >= 3 ? 30 : label.length === 2 ? 40 : 50;

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      className={className}
      role={title === undefined ? 'img' : undefined}
      aria-label={title ?? `Level ${n}`}
      style={{
        display: 'block',
        overflow: 'visible',
        filter: glow ? `drop-shadow(0 0 ${Math.round(size * 0.3)}px ${tier.glow})` : undefined,
        opacity: dim ? 0.45 : 1,
      }}
    >
      {/* Fill first so both strokes sit on top of it. */}
      <polygon points="50,3 97,50 50,97 3,50" fill={dim ? 'transparent' : 'rgba(18,13,11,0.96)'} />
      <polygon
        points="50,3 97,50 50,97 3,50"
        fill="none"
        stroke={tier.border}
        strokeWidth={9}
        strokeLinejoin="round"
      />
      <polygon
        points="50,21 79,50 50,79 21,50"
        fill="none"
        stroke={dim ? tier.border : tier.inner}
        strokeWidth={3}
        strokeLinejoin="round"
        opacity={0.9}
      />
      <text
        x="50"
        y={TEXT_Y}
        textAnchor="middle"
        dominantBaseline="central"
        fill={tier.text}
        style={{
          // Display font: a rank is ceremony, not an operational readout.
          fontFamily: 'var(--font-display, Grenze, serif)',
          fontWeight: 700,
          fontSize,
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </text>
    </svg>
  );
}
